import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import AnalyticsControls from './AnalyticsControls'
import { analyticsActive, analyticsContext, getMetaMeasurement, captureBehavior, pixelStatus, setAnalyticsPurpose } from './privacyAnalytics'
import { currentArticleSlug, rememberArticleCta } from './attribution'
import './privacy-analytics.css'

declare global {
  interface Window {
    AeloPrelaunchAnalytics?: {
      context: typeof analyticsContext
      // Optional: other pages (for example the homepage bridge) may expose only the three original members.
      measurement?: typeof getMetaMeasurement
      capture: typeof captureBehavior
      setPurpose: typeof setAnalyticsPurpose
      // What the Meta Pixel is doing on this page (state, reason, queued and failed events). Read-only.
      pixel?: typeof pixelStatus
    }
  }
}

// The editorial pages keep their published body. Mount only the shared privacy
// controls; never hydrate that body with an old application bundle.
const root = document.createElement('div')
root.id = 'aelo-static-privacy'
root.className = 'ph-no-capture'
document.body.appendChild(root)
createRoot(root).render(<BrowserRouter><AnalyticsControls /></BrowserRouter>)
window.AeloPrelaunchAnalytics = {
  context: analyticsContext,
  measurement: getMetaMeasurement,
  capture: captureBehavior,
  setPurpose: setAnalyticsPurpose,
  pixel: pixelStatus,
}

let checking = false
async function refreshPurpose() {
  if (checking) return
  checking = true
  try {
    const response = await fetch('/api/founding/config', { credentials: 'same-origin', cache: 'default' })
    const body = response.ok ? await response.json() : null
    setAnalyticsPurpose(body?.campaign?.purpose)
  } catch {
    setAnalyticsPurpose()
  } finally {
    checking = false
  }
}
void refreshPurpose()
window.addEventListener('focus', () => { void refreshPurpose() })
window.setInterval(() => { if (document.visibilityState === 'visible') void refreshPurpose() }, 30_000)

document.addEventListener('click', event => {
  const link = (event.target as Element | null)?.closest('a[href]')
  if (!link) return
  try {
    const destination = new URL(link.getAttribute('href') ?? '', location.href)
    if (destination.origin !== location.origin) return
    const article = currentArticleSlug()
    const toSignup = destination.pathname === '/' || /^\/founding\/?$/.test(destination.pathname)
    // Release 14: leaving an article for the signup page credits a signup there to the article (signups by article).
    if (article && toSignup) rememberArticleCta(article)
    if (destination.pathname === '/founding' || (article && toSignup)) {
      captureBehavior('aelo_content_cta_clicked', { source: 'editorial', path: location.pathname, destination: destination.pathname === '/' ? 'home' : 'founding', ...(article ? { article_slug: article } : {}) }, { sendInstantly: true })
    }
  } catch { /* Non-URL links do not produce analytics. */ }
})

/**
 * Release 14: how far readers get through an article. One event per threshold (25, 50, 75, 100%) per page view.
 * 100% means the bottom of the screen reached the end of the reading content (the article's FAQ), where the
 * technical record and sources begin. Thresholds reached before analytics consent are sent once consent is given.
 */
function trackArticleDepth() {
  const slug = currentArticleSlug()
  const article = document.querySelector('article')
  if (!slug || !article) return
  const thresholds = [25, 50, 75, 100]
  const reached = new Set<number>()
  const sent = new Set<number>()
  const flush = () => {
    if (!analyticsActive()) return
    for (const depth of thresholds) {
      if (!reached.has(depth) || sent.has(depth)) continue
      sent.add(depth)
      captureBehavior('aelo_article_scroll', { depth, article_slug: slug, path: location.pathname })
    }
  }
  const measure = () => {
    const top = article.getBoundingClientRect().top + window.scrollY
    const stop = article.querySelector('#technical-record, #sources, footer')
    const end = (stop ?? article).getBoundingClientRect()[stop ? 'top' : 'bottom'] + window.scrollY
    const progress = (window.scrollY + window.innerHeight - top) / Math.max(1, end - top)
    for (const depth of thresholds) if (progress * 100 >= depth) reached.add(depth)
    flush()
  }
  let queued = false
  const schedule = () => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => { queued = false; measure() })
  }
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule, { passive: true })
  window.addEventListener('load', schedule)
  window.addEventListener('aelo:analytics-ready', flush)
  schedule()
}
trackArticleDepth()
